import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.lexback.fr';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lexback_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('lexback_token');
      localStorage.removeItem('lexback_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ─── Auth ────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:    (email, password) => api.post('/api/auth/login', { email, password }),
  register: (data)            => api.post('/api/auth/register', data),
  me:       ()                => api.get('/api/auth/me'),
};

// ─── Workspace ───────────────────────────────────────────────────────────────
export const workspaceAPI = {
  get:           ()     => api.get('/api/workspace/me'),
  update:        (data) => api.patch('/api/workspace/me', data),
  getRunbook:    ()     => api.get('/api/workspace/runbook'),
  updateRunbook: (data) => api.patch('/api/workspace/runbook', data),
  connectors:    ()     => api.get('/api/workspace/connectors'),
  getConnector:  (type) => api.get(`/api/workspace/connectors/${type}`),
};

// ─── Connectors (client-facing) ───────────────────────────────────────────
export const connectorsAPI = {
  // Airtable
  testAirtable:     (data) => api.post('/api/workspace/connectors/airtable/test', data),
  saveAirtable:     (data) => api.post('/api/workspace/connectors/airtable', data),
  // Zendesk
  testZendesk:      (data) => api.post('/api/workspace/connectors/zendesk/test', data),
  zendeskListGroups:(data) => api.post('/api/workspace/connectors/zendesk/list-groups', data),
  zendeskListAgents:(data) => api.post('/api/workspace/connectors/zendesk/list-agents', data),
  zendeskListForms: (data) => api.post('/api/workspace/connectors/zendesk/list-forms', data),
  saveZendesk:      (data) => api.post('/api/workspace/connectors/zendesk', data),
  // Aircall
  testAircall:      (data) => api.post('/api/workspace/connectors/aircall/test', data),
  aircallListNumbers:(data)=> api.post('/api/workspace/connectors/aircall/list-numbers', data),
  saveAircall:      (data) => api.post('/api/workspace/connectors/aircall', data),
  // Import
  importHistory:    (type, data) => api.post(`/api/workspace/connectors/${type}/import-history`, data),
  reimportAirtable: (data) => api.post('/api/workspace/connectors/airtable/reimport', data),
  // Airtable Webhook
  setupAirtableWebhook:   () => api.post('/api/workspace/connectors/airtable/setup-webhook'),
  refreshAirtableWebhook: () => api.post('/api/workspace/connectors/airtable/refresh-webhook'),
  // Unmapped values
  unmappedValues:         () => api.get('/api/workspace/connectors/airtable/unmapped-values'),
};

// ─── Dossiers ────────────────────────────────────────────────────────────────
export const dossiersAPI = {
  list:         (params)      => api.get('/api/dossiers', { params }),
  get:          (id)          => api.get(`/api/dossiers/${id}`),
  create:       (data)        => api.post('/api/dossiers', data),
  update:       (id, data)    => api.patch(`/api/dossiers/${id}`, data),
  remove:       (id)          => api.delete(`/api/dossiers/${id}`),
  canDelete:    (id)          => api.get(`/api/dossiers/${id}/can-delete`),
  updateStatus: (id, data)    => api.patch(`/api/dossiers/${id}/status`, data),
  conformite:   (id, formData)=> api.post(`/api/dossiers/${id}/conformite`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  pappersLookup: (siren)      => api.get(`/api/dossiers/pappers/${siren}`),
  timeline:     (id, params)  => api.get(`/api/dossiers/${id}/timeline`, { params }),
  attachments:  (id)          => api.get(`/api/documents/attachments/${id}`),
  downloadAttachment: (id)    => api.get(`/api/documents/attachments/download/${id}`),
};

// ─── Dépôt rapide (Brouillons IA) ───────────────────────────────────────────
export const draftAPI = {
  create:        (data)       => api.post('/api/dossiers/draft', data),
  get:           (id)         => api.get(`/api/dossiers/draft/${id}`),
  upload:        (id, formData) => api.post(`/api/dossiers/draft/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateComment: (id, data)   => api.patch(`/api/dossiers/draft/${id}/comment`, data),
  removeAttachment: (id, attId) => api.delete(`/api/dossiers/draft/${id}/attachment/${attId}`),
  cancel:        (id)         => api.delete(`/api/dossiers/draft/${id}`),
  analyze:       (id)         => api.post(`/api/dossiers/draft/${id}/analyze`),
  submit:        (id, data)   => api.post(`/api/dossiers/draft/${id}/submit`, data),
};

// ─── Wallet ──────────────────────────────────────────────────────────────────
export const walletAPI = {
  get:          ()     => api.get('/api/wallet'),
  transactions: (p)   => api.get('/api/wallet/transactions', { params: p }),
  recharge:     (data) => api.post('/api/wallet/recharge', data),
  updateAlert:  (data) => api.patch('/api/wallet/alert', data),
};

// ─── Factures ────────────────────────────────────────────────────────────────
export const facturesAPI = {
  list: () => api.get('/api/factures'),
};

// ─── JAL ─────────────────────────────────────────────────────────────────────
export const jalAPI = {
  stats: () => api.get('/api/jal/stats'),
  list: (params) => api.get('/api/jal', { params }),
  create: (data) => api.post('/api/jal', data),
  apercu: (dossierId) => api.get(`/api/jal/${dossierId}/apercu`),
};

// ─── Aircall ─────────────────────────────────────────────────────────────────
export const aircallAPI = {
  stats: () => api.get('/api/aircall/stats'),
  calls: (params) => api.get('/api/aircall/calls', { params }),
  saveConfig: (data) => api.post('/api/aircall/config', data),
  rapport: () => api.get('/api/aircall/rapport'),
};

// ─── Team ────────────────────────────────────────────────────────────────────
export const teamAPI = {
  stats: () => api.get('/api/team/stats'),
  formalistes: () => api.get('/api/team/formalistes'),
  dossiersFormaliste: (id) => api.get(`/api/team/formalistes/${id}/dossiers`),
  setLexbackTeam: (id, isLexbackTeam) => api.patch(`/api/team/formalistes/${id}/lexback-team`, { is_lexback_team: isLexbackTeam }),
};

// ─── Admin ───────────────────────────────────────────────────────────────────
export const adminAPI = {
  // Workspaces
  workspaces:         ()                          => api.get('/api/admin/workspaces'),
  workspacesEnriched: ()                          => api.get('/api/admin/workspaces-enriched'),
  updatePlan:         (id, data)                  => api.patch(`/api/admin/workspaces/${id}/plan`, data),
  activate:           (id)                        => api.patch(`/api/admin/workspaces/${id}/activate`),
  creditWallet:       (workspaceId, montant, ref) => api.post('/api/wallet/credit', null, {
    params: { workspace_id: workspaceId, montant, reference: ref },
  }),

  // Config client (workspace + runbook)
  getConfig:      (id)         => api.get(`/api/admin/workspaces/${id}/config`),
  updateConfig:   (id, data)   => api.patch(`/api/admin/workspaces/${id}/config`, data),

  // Impersonation
  impersonate:    (userId)     => api.post(`/api/admin/impersonate/${userId}`),

  // Users
  users:          (params)     => api.get('/api/admin/users', { params }),
  userGet:        (id)         => api.get(`/api/admin/users/${id}`),
  createUser:     (data)       => api.post('/api/admin/users', data),
  updateUser:     (id, data)   => api.patch(`/api/admin/users/${id}`, data),
  activateUser:   (id)         => api.patch(`/api/admin/users/${id}/activate`),
  deactivateUser: (id)         => api.patch(`/api/admin/users/${id}/deactivate`),
  resetPassword:  (id, pwd)    => api.post(`/api/admin/users/${id}/reset-password`, { new_password: pwd }),

  // Opérations / Dossiers
  dossiers:          (params)  => api.get('/api/admin/dossiers', { params }),
  dossierDetail:     (id)      => api.get(`/api/admin/dossiers/${id}`),
  dossierDocuments:  (id)      => api.get(`/api/admin/dossiers/${id}/documents`),
  dossierTimeline:   (id)      => api.get(`/api/admin/dossiers/${id}/timeline`),

  // Alertes
  alertes:        ()           => api.get('/api/admin/alertes'),

  // Facturation
  billing:        (params)     => api.get('/api/admin/billing', { params }),

  // Stats
  stats:          ()           => api.get('/api/admin/stats'),

  // Connectors
  getConnectors:    (wsId)             => api.get(`/api/admin/workspaces/${wsId}/connectors`),
  getConnector:     (wsId, type)       => api.get(`/api/admin/workspaces/${wsId}/connectors/${type}`),
  upsertConnector:  (wsId, type, data) => api.post(`/api/admin/workspaces/${wsId}/connectors/${type}`, data),
  deleteConnector:  (wsId, type)       => api.delete(`/api/admin/workspaces/${wsId}/connectors/${type}`),
  testAirtable:     (wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/airtable/test`, data),

  // Zendesk
  testZendesk:      (wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/zendesk/test`, data),
  zendeskListGroups:(wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/zendesk/list-groups`, data),
  zendeskListAgents:(wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/zendesk/list-agents`, data),
  zendeskListForms: (wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/zendesk/list-forms`, data),

  // Aircall
  testAircall:      (wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/aircall/test`, data),
  aircallListNumbers:(wsId, data)      => api.post(`/api/admin/workspaces/${wsId}/connectors/aircall/list-numbers`, data),

  // Import
  importHistory:    (wsId, type, data) => api.post(`/api/admin/workspaces/${wsId}/connectors/${type}/import-history`, data),
  // Airtable Webhook
  setupAirtableWebhook:   (wsId) => api.post(`/api/admin/workspaces/${wsId}/connectors/airtable/setup-webhook`),
  refreshAirtableWebhook: (wsId) => api.post(`/api/admin/workspaces/${wsId}/connectors/airtable/refresh-webhook`),
  getImportJob:     (jobId)            => api.get(`/api/admin/import-jobs/${jobId}`),
  listImportJobs:   (wsId)             => api.get(`/api/admin/workspaces/${wsId}/import-jobs`),
  reimportAirtable: (wsId, data)       => api.post(`/api/admin/workspaces/${wsId}/connectors/airtable/reimport`, data),
  // Unmapped values
  unmappedValues:   (wsId)             => api.get(`/api/admin/workspaces/${wsId}/connectors/airtable/unmapped-values`),
};

// ─── Events / Orphans ───────────────────────────────────────────────────────
export const eventsAPI = {
  orphans:      (params) => api.get('/api/events/orphans', { params }),
  orphansCount: ()       => api.get('/api/events/orphans/count'),
  linkOrphan:   (id, data) => api.post(`/api/events/orphans/${id}/link`, data),
  ignoreOrphan: (id)     => api.post(`/api/events/orphans/${id}/ignore`),
  links:        (params) => api.get('/api/events/links', { params }),
  searchDossiers: (q)    => api.get('/api/events/dossiers/search', { params: { q } }),
};

// ─── Catalog (Batch 1) — pour pré-charger les dropdowns frontend ────────────
export const catalogAPI = {
  resolved: () => api.get('/api/catalog/resolved'),
};

// ─── Pilotage — benchmarks Lexback (moyennes sur tous les dossiers, tous clients) ──
// Endpoint backend à wirer (à défaut, le frontend fallback sur des moyennes Lexback
// codées en dur en attendant). Le frontend affiche TOUJOURS "Moyenne Lexback"
// comme libellé de comparaison (pas "officiel INPI") car ce sont nos chiffres.
export const pilotageAPI = {
  // Renvoie : { greffes: { paris: 10.2, lyon: 8.4, ... }, traitement: 2.1, signature: 3.4, greffe: 8.6 }
  benchmarks: () => api.get('/api/pilotage/benchmarks'),
};

export default api;

// ─── Emails (envoi client / signature + suivi) ───────────────────────────────
export const emailsAPI = {
  dossierDocuments: (dossierId)        => api.get(`/api/documents/dossier/${dossierId}`),
  sendClient:       (dossierId, data)  => api.post(`/api/dossiers/${dossierId}/send-client`, data),
  sendSignature:    (dossierId, data)  => api.post(`/api/dossiers/${dossierId}/send-signature`, data),
  list:             (params)           => api.get('/api/emails', { params }),
  get:              (id)               => api.get(`/api/emails/${id}`),
  resend:           (id)               => api.post(`/api/emails/${id}/resend`),
};
